import { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { useNavigate, useParams } from "react-router-dom";
import { Mic, MicOff, Video, VideoOff, PhoneOff, MonitorUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Brand } from "@/components/Shell";

function DailyCall({ room, onLeave }) {
  const host = useRef();
  useEffect(() => {
    let frame;
    let alive = true;
    (async () => {
      await DailyIframe.getCallInstance()?.destroy();
      if (!alive) return;
      frame = DailyIframe.createFrame(host.current, { showLeaveButton: true, lang: "it", iframeStyle: { width: "100%", height: "calc(100vh - 84px)", minHeight: "456px", border: "0", borderRadius: "24px" } });
      frame.on("left-meeting", onLeave);
      frame.join({ url: room.room_url, token: room.token });
    })();
    return () => { alive = false; frame?.destroy(); };
  }, [room.room_url, room.token, onLeave]);
  return <div ref={host} className="flex h-[calc(100vh-60px)] min-h-[480px] flex-1 p-3" data-testid="daily-call" />;
}

export default function VideoRoom() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState(null);
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(false);
  const videoRef = useRef();
  const streamRef = useRef();

  useEffect(() => {
    api.get(`/video/${aid}/token`).then(({ data }) => setRoom(data)).catch((e) => setError(errMsg(e)));
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, [aid]);

  const toggleCam = async () => {
    if (cam) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setCam(false);
      return;
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: true, audio: mic });
      videoRef.current.srcObject = streamRef.current;
      setCam(true);
    } catch {
      setError("Impossibile accedere alla videocamera");
    }
  };

  const leave = useRef(() => navigate("/area-personale")).current;
  if (room?.provider === "DAILY") {
    return (
      <div className="flex min-h-screen flex-col bg-slate-950 text-white" data-testid="video-room">
        <header className="flex items-center justify-between px-4 py-3"><Brand dark /><span className="rounded-full bg-emerald-500 px-3 py-1 text-[11px] font-bold text-emerald-950" data-testid="video-live-badge">STANZA PRIVATA · NESSUNA REGISTRAZIONE</span></header>
        {room.appointment.late_alert_at && !room.is_pro && (
          <div className="mx-3 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-amber-400 px-4 py-2 text-sm font-semibold text-amber-950" data-testid="video-late-banner">
            Il professionista è in ritardo: puoi attendere oppure spostare il consulto.
            <button data-testid="video-late-reschedule" onClick={() => navigate(`/riprogramma/${aid}`)} className="rounded-full bg-amber-950 px-4 py-1.5 text-xs font-bold text-amber-50 transition-colors hover:bg-black">Riprogramma in un tocco</button>
          </div>
        )}
        <DailyCall room={room} onLeave={leave} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-950 text-white" data-testid="video-room">
      <header className="flex items-center justify-between px-4 py-3"><Brand dark /><span className="rounded-full bg-amber-400 px-3 py-1 text-[11px] font-bold text-amber-950" data-testid="video-mock-badge">STANZA VIDEO SIMULATA · DEMO</span></header>
      {error && <p className="m-auto text-sm text-rose-300" data-testid="video-error">{error}</p>}
      {!error && !room && <p className="m-auto text-sm text-slate-400">Connessione…</p>}
      {!error && room && (
        <main className="grid flex-1 gap-3 p-3 md:grid-cols-[1fr_320px]">
          <div className="relative grid min-h-[50vh] place-items-center overflow-hidden rounded-3xl bg-slate-900">
            <div className="text-center"><p className="font-display text-2xl font-bold">{room.appointment.professional_name}</p><p className="text-sm text-slate-400">{room.appointment.service_name}</p><p className="mt-2 text-xs text-slate-500">In attesa del partecipante… (provider {room.provider})</p></div>
            <video ref={videoRef} autoPlay muted playsInline className={`absolute bottom-3 right-3 h-36 w-48 rounded-2xl border border-cyan-400/40 bg-black object-cover ${cam ? "" : "hidden"}`} />
          </div>
          <aside className="glass-dark space-y-3 rounded-3xl p-4 text-xs text-slate-300">
            <p className="font-bold text-white">Dettagli stanza privata</p>
            <p>Stanza: <span className="font-mono">{room.room}</span></p>
            <p>Token: <span className="font-mono">{room.token.slice(0, 14)}…</span></p>
            <p className="text-amber-300">{room.note}</p>
            <p>L'integrazione reale (LiveKit Cloud o Daily) genererà token firmati dal backend per stanze riservate.</p>
          </aside>
        </main>
      )}
      <footer className="flex justify-center gap-3 p-4">
        <button data-testid="video-mic-btn" onClick={() => setMic(!mic)} className="grid h-12 w-12 place-items-center rounded-full bg-slate-800 transition-colors hover:bg-slate-700">{mic ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5 text-rose-400" />}</button>
        <button data-testid="video-cam-btn" onClick={toggleCam} className="grid h-12 w-12 place-items-center rounded-full bg-slate-800 transition-colors hover:bg-slate-700">{cam ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}</button>
        <button data-testid="video-share-btn" className="grid h-12 w-12 place-items-center rounded-full bg-slate-800 opacity-60" title="Condivisione schermo (disponibile con provider reale)"><MonitorUp className="h-5 w-5" /></button>
        <button data-testid="video-leave-btn" onClick={() => navigate("/area-personale")} className="grid h-12 w-12 place-items-center rounded-full bg-rose-600 transition-colors hover:bg-rose-700"><PhoneOff className="h-5 w-5" /></button>
      </footer>
    </div>
  );
}
